import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Bell, Check, X, Loader2, Clock, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { accessControlAction } from '@/lib/api';
import { Header } from '@/components/layout/Header';

export default function IntercomApprovePage() {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [request, setRequest] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState(false);
  const [responseStatus, setResponseStatus] = useState<'approved' | 'denied' | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Store intended destination for post-login redirect
  useEffect(() => {
    if (!authLoading && !user && requestId) {
      sessionStorage.setItem('intercom_redirect', `/intercom/${requestId}`);
    }
  }, [authLoading, user, requestId]);

  // Redirect to auth if not logged in
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth');
    }
  }, [authLoading, user, navigate]);

  // Fetch request details
  useEffect(() => {
    if (!user || !requestId) return;

    async function fetchRequest() {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('intercom_requests')
        .select(`
          id, visitor_name, status, created_at, expires_at, notify_user_id,
          device:ziplink_devices (name, device_code)
        `)
        .eq('id', requestId)
        .single();

      if (fetchError || !data) {
        setError('Förfrågan hittades inte');
      } else if (data.notify_user_id !== user!.id) {
        setError('Du har inte behörighet att svara på denna förfrågan');
      } else {
        setRequest({
          ...data,
          device: Array.isArray(data.device) ? data.device[0] : data.device,
        });

        if (data.status !== 'pending') {
          setResponseStatus(data.status === 'approved' ? 'approved' : 'denied');
        }
      }
      setLoading(false);
    }

    fetchRequest();
  }, [user, requestId]);

  const handleRespond = async (action: 'approve' | 'deny') => {
    if (!requestId) return;
    setResponding(true);
    setError(null);

    const result = await accessControlAction<{ status: string }>('intercom-respond', {
      request_id: requestId,
      action,
    });

    setResponding(false);

    if (result.error) {
      setError(result.error);
    } else {
      setResponseStatus(action === 'approve' ? 'approved' : 'denied');
    }
  };

  if (authLoading || (!user && !error)) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </main>
    );
  }

  const isExpired = request && new Date(request.expires_at) < new Date() && request.status === 'pending';

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center px-4 py-6">
        <div className="w-full max-w-sm space-y-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <Card className="bg-card/80 backdrop-blur">
              <CardContent className="pt-6 text-center space-y-4">
                <AlertCircle className="h-12 w-12 text-destructive mx-auto" />
                <p className="text-sm text-destructive">{error}</p>
                <Button variant="outline" onClick={() => navigate('/keys')}>
                  Gå till nycklar
                </Button>
              </CardContent>
            </Card>
          ) : responseStatus ? (
            <Card className="bg-card/80 backdrop-blur">
              <CardContent className="pt-6 text-center space-y-4">
                {responseStatus === 'approved' ? (
                  <>
                    <div className="flex justify-center">
                      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                        <Check className="h-8 w-8 text-green-600 dark:text-green-400" />
                      </div>
                    </div>
                    <p className="text-lg font-medium">Åtkomst beviljad</p>
                    <p className="text-sm text-muted-foreground">
                      {request?.visitor_name} har fått en tillfällig nyckel
                    </p>
                  </>
                ) : (
                  <>
                    <div className="flex justify-center">
                      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                        <X className="h-8 w-8 text-red-600 dark:text-red-400" />
                      </div>
                    </div>
                    <p className="text-lg font-medium">Förfrågan nekad</p>
                  </>
                )}
                <Button variant="outline" onClick={() => navigate('/keys')}>
                  Gå till nycklar
                </Button>
              </CardContent>
            </Card>
          ) : isExpired ? (
            <Card className="bg-card/80 backdrop-blur">
              <CardContent className="pt-6 text-center space-y-4">
                <Clock className="h-12 w-12 text-orange-500 mx-auto" />
                <p className="text-lg font-medium">Förfrågan har gått ut</p>
                <Button variant="outline" onClick={() => navigate('/keys')}>
                  Gå till nycklar
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-card/80 backdrop-blur border-primary/30">
              <CardContent className="pt-6 space-y-6">
                <div className="text-center space-y-2">
                  <div className="flex justify-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 border-2 border-primary/30">
                      <Bell className="h-8 w-8 text-primary" />
                    </div>
                  </div>
                  <p className="text-lg font-bold">{request?.visitor_name}</p>
                  <p className="text-sm text-muted-foreground">
                    söker dig vid {request?.device?.name || 'enhet'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(request?.created_at).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>

                <div className="flex gap-3">
                  <Button
                    className="flex-1"
                    variant="outline"
                    size="lg"
                    onClick={() => handleRespond('deny')}
                    disabled={responding}
                  >
                    {responding ? <Loader2 className="h-5 w-5 animate-spin" /> : <><X className="h-5 w-5" /> Neka</>}
                  </Button>
                  <Button
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                    size="lg"
                    onClick={() => handleRespond('approve')}
                    disabled={responding}
                  >
                    {responding ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Check className="h-5 w-5" /> Godkänn</>}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
